import type { ApolloError } from "@apollo/client";
import type { AxiosError } from "axios";
import { noop, pick } from "lodash";
import { AlertCircle } from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { SuperAdminOnly } from "@/components/shared/miscellaneous/superadmin-only";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ErrorMessage } from "@/components/ui/error";
import { Label } from "@/components/ui/label";
import { Link } from "@/components/ui/link";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { LoadingSpinner } from "@/components/ui/spinner";
import type { InvoiceTypeString } from "@/lib/constants/invoice-type";
import { cn } from "@/lib/utils";
import { useTenant } from "@/providers/session";
import { useGetAllDatevTokens } from "@/queries/datev-tokens/get-all";
import { useGetInvoiceExportGroups } from "@/queries/invoices/export-groups";

enum ErrorCode {
  DATEV = "datev",
  UNKNOWN = "unknown",
}

interface MonthSelectionDialogProps {
  isOpen: boolean;
  onMonthSelect?: (months: string[], isSelected: boolean) => void;
  onConfirm: (groupedInvoices: Record<string, string[]>) => Promise<void>;
  onGenerateDatevFormatFiles: (
    groupedInvoices: Record<string, string[]>,
  ) => Promise<void>;
  onCancel: () => void;
  disallowExport?: boolean;
  invoiceIds: string[];
  exportAll: boolean;
  invoiceType?: InvoiceTypeString;
}

export const MonthSelectionDialog: React.FC<MonthSelectionDialogProps> = memo(
  ({
    isOpen,
    onMonthSelect = noop,
    onConfirm,
    onGenerateDatevFormatFiles,
    onCancel,
    disallowExport,
    exportAll,
    invoiceIds,
    invoiceType,
  }) => {
    const { t } = useTranslation();
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState<ErrorCode | null>(null);
    const [monthToMoveTo, setMonthToMoveTo] = useState<string | undefined>();
    const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
    const [groupBy, setGroupBy] = useState<"invoiceDate" | "serviceDate">(
      "invoiceDate",
    );
    const { companySlug } = useTenant();

    const { tokens, loading } = useGetAllDatevTokens({
      fetchPolicy: "network-only",
      skipPermissionsCheck: false,
    });

    const token = tokens?.[0];

    const {
      invoiceExportGroups: invoiceGroups,
      hasPendingClientApproval,
      hasMissingDates,
      loading: invoiceGroupsLoading,
      error: invoiceGroupsError,
      refetch: invoiceGroupsRefetch,
    } = useGetInvoiceExportGroups({
      invoiceIds,
      exportAll,
      groupBy,
      invoiceType,
    });

    const months = useMemo(() => {
      if (invoiceGroups) {
        return Object.keys(invoiceGroups);
      }
      return [];
    }, [invoiceGroups]);

    useEffect(() => {
      if (isOpen) {
        setSelectedMonths(months);
        setMonthToMoveTo(undefined);
      } else {
        setSelectedMonths([]);
        setMonthToMoveTo(undefined);
      }
    }, [isOpen, months, loading]);

    const handleMonthSelection = (months: string[], isSelected: boolean) => {
      if (isSelected) {
        setSelectedMonths((prevMonths) => [
          ...prevMonths.filter((m) => !months.includes(m)),
          ...months,
        ]);
      } else {
        setSelectedMonths((prevMonths) =>
          prevMonths.filter((m) => !months.includes(m)),
        );
      }
    };

    useEffect(() => {
      onMonthSelect(selectedMonths);
    }, [selectedMonths]);

    const handleSelectAll = (checked: boolean) => {
      handleMonthSelection(months, checked);
    };

    const handleMonthSelect = (month: string, isSelected: boolean) => {
      handleMonthSelection([month], isSelected);
    };

    const groupedInvoices = useMemo(() => {
      let groupedInvoices: Record<string, string[]>;

      if (monthToMoveTo) {
        const _months = selectedMonths;

        const invoiceGroupsToExport = pick(invoiceGroups, _months);

        const monthsGreaterThan = _months.filter(
          (month) => month > monthToMoveTo,
        );
        const monthsLessThan = _months.filter((month) => month < monthToMoveTo);

        const groupedInvoicesGreaterThan = pick(
          invoiceGroupsToExport,
          monthsGreaterThan,
        );
        const groupedInvoicesLessThan = pick(invoiceGroupsToExport, [
          ...monthsLessThan,
          monthToMoveTo,
        ]);

        const _groupedInvoices = {
          ...groupedInvoicesGreaterThan,
          [monthToMoveTo]: Object.values(groupedInvoicesLessThan).flat(),
        };
        groupedInvoices = _groupedInvoices;
      } else {
        groupedInvoices = pick(invoiceGroups, selectedMonths);
      }

      return groupedInvoices;
    }, [invoiceGroups, monthToMoveTo, selectedMonths]);

    const handleConfirm = async () => {
      setIsExporting(true);

      try {
        await onConfirm(groupedInvoices);
      } catch (_error) {
        const error = _error as AxiosError<{ code: string; message: string }>;
        const errorData = error.response?.data;

        if (errorData?.code?.includes("EDI")) {
          setError(ErrorCode.DATEV);
          return;
        }
        setError(ErrorCode.UNKNOWN);
      } finally {
        setIsExporting(false);
      }
    };

    const handleConfirmGenerateDatevFormatFiles = async () => {
      setIsExporting(true);

      try {
        await onGenerateDatevFormatFiles(groupedInvoices);
      } finally {
        setIsExporting(false);
      }
    };

    const checkedState = useMemo(() => {
      if (selectedMonths.length === 0) return false;
      if (selectedMonths.length === months.length) return true;
      return "indeterminate";
    }, [selectedMonths, months]);

    const tokenIssues = useMemo(() => {
      return (
        new Date(token?.refreshTokenExpirationDate) < new Date() || token?.error
      );
    }, [token]);

    const handleOnOpenChange = (open: boolean) => {
      if (!open) {
        setError(null);
      }
    };

    const handleGroupByChange = (groupBy: "invoiceDate" | "serviceDate") => {
      setGroupBy(groupBy);
    };

    if (loading) return null;

    return (
      <AlertDialog open={isOpen} onOpenChange={handleOnOpenChange}>
        <AlertDialogContent className="max-h-[90vh] max-w-3xl flex flex-col p-0 gap-0">
          <div className="border-b bg-white px-6 py-3 rounded-t-lg">
            <AlertDialogTitle className="text-2xl">
              {t("exports.month_selection_dialog.dialog_title")}
            </AlertDialogTitle>
          </div>
          <div className="flex-1 overflow-y-auto px-6">
            <div className="flex flex-col gap-4 py-4">
              {tokenIssues && (
                <Alert variant="warning">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{t("exports.token_issues.title")}</AlertTitle>
                  <AlertDescription className="whitespace-pre-line">
                    {t("exports.token_issues.description.title")}
                  </AlertDescription>
                  <ul className="list-decimal ml-2">
                    <li>
                      <Trans
                        i18nKey={"exports.token_issues.description.step_1"}
                        components={{
                          Link: (
                            <Link
                              className="underline font-semibold"
                              href={{
                                pathname:
                                  "/$company/buchhaltung/einstellungen/integrationen",
                                query: { company: companySlug },
                              }}
                            />
                          ),
                        }}
                      />
                    </li>
                    <li>{t("exports.token_issues.description.step_2")}</li>
                    <li>{t("exports.token_issues.description.step_3")}</li>
                  </ul>
                </Alert>
              )}
              {error === ErrorCode.DATEV && (
                <Alert variant="warning">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{t("exports.datev_errors.title")}</AlertTitle>
                  <AlertDescription className="whitespace-pre-line">
                    {t("exports.datev_errors.description")}
                  </AlertDescription>
                </Alert>
              )}
              {hasPendingClientApproval && (
                <Alert variant="warning">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>
                    {t("exports.pending_client_approval.title")}
                  </AlertTitle>
                  <AlertDescription className="whitespace-pre-line">
                    {t("exports.pending_client_approval.description")}
                  </AlertDescription>
                </Alert>
              )}
              {hasMissingDates && (
                <Alert variant="warning">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{t("exports.missing_dates.title")}</AlertTitle>
                  <AlertDescription className="whitespace-pre-line">
                    {t("exports.missing_dates.description")}
                  </AlertDescription>
                </Alert>
              )}
              <DialogContent
                months={months}
                checkedState={checkedState}
                handleSelectAll={handleSelectAll}
                handleMonthSelect={handleMonthSelect}
                monthToMoveTo={monthToMoveTo}
                setMonthToMoveTo={setMonthToMoveTo}
                invoiceGroups={invoiceGroups}
                selectedMonths={selectedMonths}
                invoiceGroupsLoading={invoiceGroupsLoading}
                invoiceGroupsError={invoiceGroupsError}
                invoiceGroupsRefetch={invoiceGroupsRefetch}
                groupBy={groupBy}
                handleGroupByChange={handleGroupByChange}
              />
            </div>
          </div>
          <div className="border-t bg-white px-6 py-3 rounded-b-lg">
            <AlertDialogFooter>
              <AlertDialogCancel onClick={onCancel}>
                {t("cancel")}
              </AlertDialogCancel>
              <SuperAdminOnly>
                <AlertDialogAction
                  onClick={handleConfirmGenerateDatevFormatFiles}
                  disabled={isExporting || invoiceGroupsLoading}
                  className="gap-1 bg-background border text-primary hover:bg-gray-100 bg-pink-400"
                >
                  {isExporting && <LoadingSpinner />}
                  {`DATEV-Format (${
                    Object.values(groupedInvoices).flat().length
                  })`}
                </AlertDialogAction>
              </SuperAdminOnly>
              {token && !disallowExport && (
                <AlertDialogAction
                  onClick={handleConfirm}
                  disabled={tokenIssues || isExporting || invoiceGroupsLoading}
                  className="gap-1"
                >
                  {isExporting && <LoadingSpinner />}
                  {t("exports.month_selection_dialog.export", {
                    count: Object.values(groupedInvoices).flat().length,
                  })}
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    );
  },
);

const DialogContent = ({
  months,
  checkedState,
  handleSelectAll,
  handleMonthSelect,
  monthToMoveTo,
  setMonthToMoveTo,
  invoiceGroups,
  selectedMonths,
  invoiceGroupsLoading,
  invoiceGroupsError,
  invoiceGroupsRefetch,
  groupBy,
  handleGroupByChange,
}: {
  months: string[];
  checkedState: boolean | "indeterminate";
  handleSelectAll: (checked: boolean) => void;
  handleMonthSelect: (month: string, isSelected: boolean) => void;
  monthToMoveTo: string | undefined;
  setMonthToMoveTo: (month: string | undefined) => void;
  invoiceGroups: Record<string, string[]>;
  selectedMonths: string[];
  invoiceGroupsLoading: boolean;
  invoiceGroupsError: ApolloError | undefined;
  invoiceGroupsRefetch: () => void;
  groupBy: "invoiceDate" | "serviceDate";
  handleGroupByChange: (groupBy: "invoiceDate" | "serviceDate") => void;
}) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] =
    useState<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    const checkScrollable = () => {
      if (scrollAreaRef.current) {
        const { scrollHeight, clientHeight } = scrollAreaRef.current;
        setShowScrollIndicator(scrollHeight > clientHeight + 10);
      }
    };

    checkScrollable();
    const timeoutId = setTimeout(checkScrollable, 100);

    return () => clearTimeout(timeoutId);
  }, [invoiceGroupsLoading, months]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    setShowScrollIndicator(scrollTop + clientHeight < scrollHeight - 10);
  };

  if (invoiceGroupsError) {
    return (
      <div className="flex justify-center items-center h-[200px]">
        <ErrorMessage retry={invoiceGroupsRefetch} />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col -mb-1 -mt-2">
        <AlertDialogTitle>
          {t("exports.month_selection_dialog.group_by")}
        </AlertDialogTitle>
        <AlertDialogDescription>
          <p>{t("exports.month_selection_dialog.group_by_description")}</p>
        </AlertDialogDescription>
      </div>
      <div>
        <RadioGroup
          value={groupBy}
          onValueChange={handleGroupByChange}
          orientation="horizontal"
          className="flex flex-row gap-4"
        >
          <div className="flex-shrink-0 flex items-center space-x-2">
            <RadioGroupItem value="invoiceDate" id="invoiceDate" />
            <Label htmlFor="invoiceDate" className="cursor-pointer">
              {t("invoice_date")}
            </Label>
          </div>
          <div className="flex-shrink-0 flex items-center space-x-2">
            <RadioGroupItem value="serviceDate" id="serviceDate" />
            <Label htmlFor="serviceDate" className="cursor-pointer">
              {t("service_date")}
            </Label>
          </div>
        </RadioGroup>
      </div>
      <div className="flex flex-col mt-2 -mb-2">
        <AlertDialogTitle>
          {t("exports.month_selection_dialog.title")}
        </AlertDialogTitle>
        <AlertDialogDescription>
          <p>{t("exports.month_selection_dialog.description")}</p>
        </AlertDialogDescription>
      </div>
      <div className="relative">
        <ScrollArea
          className="max-h-[30vh] overflow-y-auto"
          onScroll={handleScroll}
          ref={scrollAreaRef}
        >
          {invoiceGroupsLoading ? (
            <LoadingSkeleton />
          ) : (
            <div className="flex flex-col gap-2 pb-2">
              {months.length > 1 && (
                <label className="flex items-center space-x-2">
                  <Checkbox
                    checked={checkedState}
                    onCheckedChange={(checked) => handleSelectAll(!!checked)}
                  />
                  <span>{t("exports.month_selection_dialog.select_all")}</span>
                </label>
              )}
              {months
                .sort((a, b) => {
                  if (a === "not_booked") return 1;
                  if (b === "not_booked") return -1;
                  const [yearA, monthA] = a.split("-").map(Number);
                  const [yearB, monthB] = b.split("-").map(Number);
                  if (yearA !== yearB) return yearB - yearA;
                  return monthB - monthA;
                })
                .map((month) => {
                  if (month === "not_booked") {
                    const invoices = invoiceGroups.not_booked || [];

                    if (invoices.length === 0) {
                      return null;
                    }

                    return (
                      <label
                        key="not_booked"
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedMonths.includes(month)}
                          onCheckedChange={(checked) =>
                            handleMonthSelect(month, !!checked)
                          }
                        />
                        <span>{t("not_booked")}</span>
                        <span className="text-gray-500 text-sm">
                          ({invoices.length}{" "}
                          {invoices.length === 1 ? "Dokument" : "Dokumente"})
                        </span>
                      </label>
                    );
                  }

                  const [year, monthNum] = month.split("-");
                  const date = new Date(
                    Number.parseInt(year),
                    Number.parseInt(monthNum) - 1,
                  );
                  const formattedMonth = new Intl.DateTimeFormat("de-DE", {
                    month: "short",
                    year: "numeric",
                  }).format(date);

                  const monthInvoices = invoiceGroups[month] || [];

                  return (
                    <label
                      key={month}
                      className="flex items-center space-x-2 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedMonths.includes(month)}
                        onCheckedChange={(checked) =>
                          handleMonthSelect(month, !!checked)
                        }
                      />
                      <span>{formattedMonth}</span>
                      <span className="text-gray-500 text-sm">
                        ({monthInvoices.length}{" "}
                        {monthInvoices.length === 1 ? "Rechnung" : "Rechnungen"}
                        )
                      </span>
                    </label>
                  );
                })}
            </div>
          )}
        </ScrollArea>
        {showScrollIndicator && (
          <div className="absolute bottom-0 left-0 right-0 text-center py-2 bg-gradient-to-t from-white via-white/90 to-transparent">
            <span className="text-sm text-gray-500">
              ↓ {t("exports.month_selection_dialog.scroll_for_more")}
            </span>
          </div>
        )}
      </div>
      <div className="-mb-1">
        <AlertDialogTitle>
          {t("exports.month_selection_dialog.move-invoices.title")}
        </AlertDialogTitle>
        <AlertDialogDescription>
          <p>{t("exports.month_selection_dialog.move-invoices.description")}</p>
        </AlertDialogDescription>
      </div>
      <div className="flex flex-col gap-2 pb-2">
        <div className="flex flex-row gap-2 items-center">
          <Checkbox
            checked={Boolean(monthToMoveTo)}
            disabled={invoiceGroupsLoading}
            onCheckedChange={(checked) => {
              if (!checked) {
                setMonthToMoveTo(undefined);
              } else {
                setMonthToMoveTo(months[0]);
              }
            }}
          />
          <div className="flex-1 flex flex-row gap-2 items-center">
            <p
              className={cn(
                "whitespace-nowrap",
                "text-sm",
                !monthToMoveTo && "opacity-50",
              )}
            >
              {t("exports.month_selection_dialog.move-invoices.label")}
            </p>
            <Select
              onValueChange={setMonthToMoveTo}
              value={monthToMoveTo ?? ""}
              disabled={invoiceGroupsLoading}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue
                  placeholder={t(
                    "exports.month_selection_dialog.move-invoices.placeholder",
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {months
                  .filter((month) => month !== "not_booked")
                  .reduce((acc: string[], month) => {
                    // If this is the first month, initialize the array
                    if (acc.length === 0) {
                      // Find the oldest month from the input array
                      const oldestMonth = months
                        .filter((m) => m !== "not_booked")
                        .reduce((oldest, current) => {
                          if (current === "not_booked") return oldest;
                          const [yearA, monthA] = oldest.split("-").map(Number);
                          const [yearB, monthB] = current
                            .split("-")
                            .map(Number);
                          if (yearA < yearB) return oldest;
                          if (yearA > yearB) return current;
                          return monthA <= monthB ? oldest : current;
                        });

                      const [year, monthNum] = oldestMonth
                        .split("-")
                        .map(Number);
                      const today = new Date();
                      const endYear = today.getFullYear();
                      const endMonth = today.getMonth() + 1;

                      let currentYear = year;
                      let currentMonth = monthNum;

                      while (
                        currentYear < endYear ||
                        (currentYear === endYear && currentMonth <= endMonth)
                      ) {
                        acc.push(
                          `${currentYear}-${currentMonth
                            .toString()
                            .padStart(2, "0")}`,
                        );

                        currentMonth++;
                        if (currentMonth > 12) {
                          currentMonth = 1;
                          currentYear++;
                        }
                      }
                    }
                    return acc;
                  }, [])
                  .map((month) => {
                    const [year, monthNum] = month.split("-");
                    const date = new Date(
                      Number.parseInt(year),
                      Number.parseInt(monthNum) - 1,
                    );
                    const formattedMonth = new Intl.DateTimeFormat("de-DE", {
                      month: "short",
                      year: "numeric",
                    }).format(date);

                    return (
                      <SelectItem key={month} value={month}>
                        {formattedMonth}
                      </SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </>
  );
};

const LoadingSkeleton = () => {
  return (
    <div className="flex flex-col gap-2 py-2">
      <div className="flex items-center space-x-2">
        <Skeleton className="h-4 w-4 rounded" />
        <Skeleton className="h-4 w-48" />
      </div>

      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex items-center space-x-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-32 opacity-60" />
        </div>
      ))}
    </div>
  );
};
